import { Suspense, lazy } from 'react';

import { Box } from '@betterment/samba-react';
import styled from '@emotion/styled';

import GoalImageLoader from './GoalImageLoader';

import type { SystemStyleObject } from '@betterment/samba-styled-system/types';

export const images = Object.freeze({
  apple: lazy(() => import('./AppleLight')),
  baby: lazy(() => import('./BabyLight')),
  bandaid: lazy(() => import('./BandaidLight')),
  business: lazy(() => import('./BusinessLight')),
  cap: lazy(() => import('./CapLight')),
  car: lazy(() => import('./CarLight')),
  card: lazy(() => import('./CardLight')),
  charity: lazy(() => import('./CharityLight')),
  coin: lazy(() => import('./CoinLight')),
  dollar: lazy(() => import('./DollarLight')),
  home: lazy(() => import('./HomeLight')),
  lifering: lazy(() => import('./LiferingLight')),
  parasol: lazy(() => import('./ParasolLight')),
  plane: lazy(() => import('./PlaneLight')),
  plant: lazy(() => import('./PlantLight')),
  present: lazy(() => import('./PresentLight')),
  tree: lazy(() => import('./TreeLight')),
  watering_can: lazy(() => import('./WateringCanLight')),
  wedding: lazy(() => import('./WeddingLight')),
});

export interface GoalImageProps {
  name: keyof typeof images | 'custom';
  size?: string;
  rounded: boolean;
  className?: string;
  mode?: string;
  css?: SystemStyleObject;
}

export interface ImageProps {
  rounded: boolean;
}

export const Image = styled.img`
  max-width: 100%;
`;

export const RoundedEdgeImage = styled(Image)`
  border-radius: 0.8rem;
`;

const GoalImageComponent: React.FC<React.PropsWithChildren<GoalImageProps>> = ({
  className,
  size = '184px',
  name,
  rounded = false,
  css: cssProp,
}) => {
  const ImageComponent = name === 'custom' ? images.present : images[name];

  return (
    <Suspense fallback={<GoalImageLoader size={size} rounded={rounded} />}>
      <Box
        className={className}
        css={{ display: 'flex', ...cssProp }}
        style={{ width: size, minWidth: size, maxWidth: size }}
      >
        <ImageComponent rounded={rounded} />
      </Box>
    </Suspense>
  );
};

export const GoalImage: React.FC<React.PropsWithChildren<GoalImageProps>> = ({
  className,
  size = '184px',
  name,
  rounded = false,
  css: cssProp,
}) => {
  return (
    <GoalImageComponent
      className={className}
      size={size}
      name={name as keyof typeof images}
      rounded={rounded}
      css={cssProp}
    />
  );
};
